import React from 'react';
import FormField from '../shared/FormField';
import SelectField from '../shared/SelectField';
import DateTimeField from '../shared/DateTimeField';
import TextAreaField from '../shared/TextAreaField';
import { useEventTypes, useRequiresAircraftInfo } from '../hooks/useFormData';

const EventBasicDetails = ({ 
  formData, 
  updateFormData, 
  locations,
  eventCategories,
  eventTypes,
  aircraftTypes,
  vendors,
  stations,
  errors = {},
  loading = false 
}) => {
  const handleFieldChange = (updates) => {
    updateFormData(updates);
  };

  const filteredEventTypes = useEventTypes(formData.event_category_id, eventTypes);
  const requiresAircraftInfo = useRequiresAircraftInfo(formData.event_category_id, eventCategories);

  return (
    <div className="space-y-6">
      {/* Section Header */}
      <div className="border-b border-gray-200 pb-4">
        <div className="flex items-center space-x-3">
          <div className="flex-shrink-0">
            <div className="flex items-center justify-center w-10 h-10 bg-green-100 rounded-full">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
          </div>
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Event Basic Details</h2>
            <p className="text-sm text-gray-600 mt-1">
              Provide basic information about the safety event that occurred.
            </p>
          </div>
        </div>
      </div>

      {/* Event Date/Time and Location */}
      <div className="space-y-6">
        {/* Event Title is now generated by AI and shown only on review page */}
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <DateTimeField
            label="Event Date & Time"
            name="event_date_time"
            required
            value={formData.event_date_time}
            onChange={handleFieldChange}
            error={errors.event_date_time}
            helpText="When did the event occur?"
            disabled={loading}
          />

          <SelectField
            label="Location"
            name="location_id"
            required
            value={formData.location_id}
            onChange={handleFieldChange}
            options={locations}
            optionValue="id"
            optionLabel="location_name"
            error={errors.location_id}
            placeholder="Select location"
            helpText="Where did the event occur?"
            loading={loading}
            disabled={loading}
          />
        </div>
      </div>

      {/* Operational Station (Optional) */}
      <SelectField
        label="Operational Station (if applicable)"
        name="operational_station_id"
        value={formData.operational_station_id}
        onChange={handleFieldChange}
        options={stations}
        optionValue="id"
        optionLabel="station_name"
        error={errors.operational_station_id}
        placeholder="Select if related to line station operations"
        helpText="Select if this event is related to specific line station operations"
        loading={loading}
        disabled={loading}
      />

      <TextAreaField
        label="Location Description"
        name="location_description"
        rows={2}
        value={formData.location_description}
        onChange={handleFieldChange}
        error={errors.location_description}
        placeholder="Additional details about the specific location (e.g., 'Bay 3, near the engine stand')"
        helpText="Provide specific details about where exactly the event occurred"
        disabled={loading}
      />

      {/* Event Classification */}
      <div className="border-t border-gray-100 pt-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Event Classification</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <SelectField
            label="Event Category"
            name="event_category_id"
            required
            value={formData.event_category_id}
            onChange={(updates) => {
              // Reset event type when category changes
              if (updates.event_category_id !== formData.event_category_id) {
                handleFieldChange({ 
                  ...updates, 
                  event_type_id: null 
                });
              } else {
                handleFieldChange(updates);
              }
            }}
            options={eventCategories}
            optionValue="id"
            optionLabel="category_name"
            error={errors.event_category_id}
            placeholder="Select event category"
            helpText="Choose the main category that best describes this event"
            loading={loading}
            disabled={loading}
          />

          {formData.event_category_id && (
            <SelectField
              label="Event Type"
              name="event_type_id"
              value={formData.event_type_id}
              onChange={handleFieldChange}
              options={filteredEventTypes}
              optionValue="id"
              optionLabel="type_name"
              error={errors.event_type_id}
              placeholder="Select event type"
              helpText="Choose the specific type of event (optional)"
              loading={loading}
              disabled={loading}
            />
          )}
        </div>
      </div>

      {/* Aircraft Information (Conditional) */}
      {requiresAircraftInfo && (
        <div className="border-t border-gray-100 pt-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <div className="flex items-center space-x-2">
              <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                <path d="M13 7H7v6h6V7z" />
                <path fillRule="evenodd" d="M7 2a1 1 0 012 0v1h2V2a1 1 0 112 0v1h2a2 2 0 012 2v2H3V5a2 2 0 012-2h2V2z" clipRule="evenodd" />
              </svg>
              <h3 className="text-lg font-medium text-blue-900">Aircraft Information</h3>
            </div>
            <p className="text-sm text-blue-700 mt-1">
              This event category requires aircraft information. Please provide details below.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormField
              label="Aircraft Registration"
              name="aircraft_registration"
              type="text"
              value={formData.aircraft_registration}
              onChange={handleFieldChange}
              error={errors.aircraft_registration}
              placeholder="e.g., HS-ABC"
              maxLength={20}
              helpText="Aircraft registration number"
              disabled={loading}
            />

            <SelectField
              label="Aircraft Type"
              name="aircraft_type_id"
              value={formData.aircraft_type_id}
              onChange={handleFieldChange}
              options={aircraftTypes}
              optionValue="id"
              optionLabel="type_name"
              error={errors.aircraft_type_id}
              placeholder="Select aircraft type"
              loading={loading}
              disabled={loading}
            />

            <FormField
              label="Tail Number"
              name="aircraft_tail_number"
              type="text"
              value={formData.aircraft_tail_number}
              onChange={handleFieldChange}
              error={errors.aircraft_tail_number}
              placeholder="Aircraft tail number"
              maxLength={20}
              disabled={loading}
            />

            <SelectField
              label="Maintenance Check Type"
              name="maintenance_check_type"
              value={formData.maintenance_check_type}
              onChange={handleFieldChange}
              options={[
                { value: 'A-Check', label: 'A-Check' },
                { value: 'B-Check', label: 'B-Check' },
                { value: 'C-Check', label: 'C-Check' },
                { value: 'D-Check', label: 'D-Check' },
                { value: 'Line', label: 'Line Maintenance' }
              ]}
              error={errors.maintenance_check_type}
              placeholder="Select check type"
              disabled={loading}
            />

            <FormField
              label="Work Order Number"
              name="work_order_number"
              type="text"
              value={formData.work_order_number}
              onChange={handleFieldChange}
              error={errors.work_order_number}
              placeholder="Work order reference number"
              maxLength={50}
              disabled={loading}
            />

            <SelectField
              label="Vendor/Contractor"
              name="vendor_id"
              value={formData.vendor_id}
              onChange={handleFieldChange}
              options={vendors}
              optionValue="id"
              optionLabel="supplier_name"
              error={errors.vendor_id}
              placeholder="Select if vendor involved"
              helpText="Select if a vendor or contractor was involved"
              loading={loading}
              disabled={loading}
            />
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
          <span className="ml-2 text-sm text-gray-600">Loading event information...</span>
        </div>
      )}
    </div>
  );
};

export default EventBasicDetails;
